import { useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  Title,
} from '@mantine/core';
import qrcode from 'qrcode-generator';
import { Link, useNavigate } from 'react-router';
import type { IncidentSummary } from '@isg/shared-types';
import { useOlayOptions, useOlaylar, useQrUret } from '../api/queries';
import { OLAY_AKIS_ETIKET, OLAY_SUZGEC_DURUM_ETIKET, olayDurumEtiketi } from '../i18n/tr';
import { DofSayaci } from './olay-dof-sayaci';
import { BosListeNotu, QueryStatus } from './parts';
import { ANLAM } from '../theme';
import { tarihGoster } from '../lib/format';

/**
 * Olay listesi (F3B·S2 — Kapsam §16.8/14). Bahçe filtresi API'DEDİR (§2.16);
 * buradaki süzgeçler yalnız görünümü daraltır:
 *
 * - Durum/tür/işyeri süzgeçleri + MAJÖR ARAŞTIRMA süzgeci (türetilmiş işaret
 *   — ayrı ekran YOK: listede süzgeç, raporda sayaç; 17.08.2026 kararı).
 *   ✏️ T2·0: "Kapandı" süzgeci TÜRETİLMİŞ alandan (`kapali`) — token değil;
 *   DÖF sayacı satırda durum göstergesidir (7-j.5).
 * - RED EDİLENLER KAYBOLMAZ (R7 — KPI dışıdır ama kayıttadır): varsayılan
 *   görünümden ayrı süzgeçle gelir.
 */
export function OlaylarScreen(): React.JSX.Element {
  const olaylar = useOlaylar();
  const options = useOlayOptions();
  const navigate = useNavigate();
  const [qrAcik, setQrAcik] = useState(false);

  const [durum, setDurum] = useState<string | null>(null);
  const [tur, setTur] = useState<string | null>(null);
  const [isyeri, setIsyeri] = useState<string | null>(null);
  const [yalnizMajor, setYalnizMajor] = useState(false);
  const [redGoster, setRedGoster] = useState(false);

  const rows = useMemo(() => {
    const hepsi = olaylar.data ?? [];
    return hepsi.filter((olay) => {
      if (!redGoster && olay.durum === 'rejected') return false;
      if (redGoster && olay.durum !== 'rejected') return false;
      // Süzgeç GÖRÜNÜM seçeneğidir: "kapali" türetilmiş alandan, kalanlar
      // token'dan — açık kayıt süzgecinde kapalı olan gösterilmez.
      if (durum === 'kapali' && !olay.kapali) return false;
      if (durum && durum !== 'kapali' && (olay.durum !== durum || olay.kapali)) return false;
      if (tur && (olay.olayTuruAd ?? 'kaza') !== tur) return false;
      if (isyeri && olay.workplaceId !== isyeri) return false;
      if (yalnizMajor && !olay.majorArastirma) return false;
      return true;
    });
  }, [olaylar.data, durum, tur, isyeri, yalnizMajor, redGoster]);

  const isyerleri = useMemo(() => {
    const map = new Map<string, string>();
    for (const olay of olaylar.data ?? []) map.set(olay.workplaceId, olay.workplaceAd);
    return [...map.entries()].map(([value, label]) => ({ value, label }));
  }, [olaylar.data]);
  const turler = useMemo(() => {
    const set = new Set((olaylar.data ?? []).map((olay) => olay.olayTuruAd ?? 'kaza'));
    return [...set].map((ad) => ({ value: ad, label: ad === 'kaza' ? 'Kaza (uzman kaydı)' : ad }));
  }, [olaylar.data]);

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={3}>Olaylar</Title>
        <Group>
          {/* QR üretme + "Diğer" kuyruğu — yetki SUNUCUDAN (`qrUretebilir` =
              olay_inceleme üyeliği; madde 64 / T3). */}
          {options.data?.qrUretebilir && (
            <>
              <Button
                variant="default"
                data-testid="olay-qr-uret-ac"
                onClick={() => setQrAcik(true)}
              >
                Anonim bildirim QR'ı
              </Button>
              <Button
                variant="default"
                component={Link}
                to="/sozluk-bakim"
                data-testid="olay-diger-kuyrugu"
              >
                Sözlük bakımı
              </Button>
            </>
          )}
          <Button data-testid="olay-bildir" onClick={() => void navigate('/olaylar/yeni')}>
            Yeni bildirim
          </Button>
        </Group>
      </Group>
      {options.data?.qrUretebilir && (
        <QrUretModal
          acik={qrAcik}
          kapat={() => setQrAcik(false)}
          workplaces={options.data.workplaces}
          anahtarAcik={options.data.anonimBildirimAcik}
        />
      )}
      <Group>
        <Select
          placeholder="Durum"
          clearable
          data-testid="olay-suzgec-durum"
          data={Object.entries(OLAY_SUZGEC_DURUM_ETIKET).map(([value, label]) => ({
            value,
            label,
          }))}
          value={durum}
          onChange={setDurum}
        />
        <Select
          placeholder="Tür"
          clearable
          data-testid="olay-suzgec-tur"
          data={turler}
          value={tur}
          onChange={setTur}
        />
        <Select
          placeholder="İşyeri"
          clearable
          searchable
          data-testid="olay-suzgec-isyeri"
          data={isyerleri}
          value={isyeri}
          onChange={setIsyeri}
        />
        <Switch
          label="Yalnız majör araştırma"
          data-testid="olay-suzgec-major"
          checked={yalnizMajor}
          onChange={(event) => setYalnizMajor(event.currentTarget.checked)}
        />
        <Switch
          label="Reddedilenler"
          data-testid="olay-suzgec-red"
          checked={redGoster}
          onChange={(event) => setRedGoster(event.currentTarget.checked)}
        />
      </Group>
      <QueryStatus queries={[olaylar]} />
      {olaylar.isSuccess && rows.length === 0 && (
        <BosListeNotu mesaj="Görüntülenecek olay kaydı yok." />
      )}
      {rows.length > 0 && <OlayTablosu rows={rows} />}
    </Stack>
  );
}

function OlayTablosu({ rows }: { rows: IncidentSummary[] }): React.JSX.Element {
  return (
    <Table striped withTableBorder data-testid="olay-listesi">
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Takip no</Table.Th>
          <Table.Th>Tür</Table.Th>
          <Table.Th>Akış</Table.Th>
          <Table.Th>İşyeri</Table.Th>
          <Table.Th>Tarih</Table.Th>
          <Table.Th>Durum</Table.Th>
          <Table.Th>DÖF</Table.Th>
          <Table.Th>İşaretler</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {rows.map((olay) => (
          <Table.Tr key={olay.id} data-testid={`olay-satir-${olay.takipNo}`}>
            <Table.Td>
              <Link to={`/olaylar/${olay.id}`}>{olay.takipNo}</Link>
            </Table.Td>
            <Table.Td>{olay.olayTuruAd ?? 'Kaza (uzman kaydı)'}</Table.Td>
            <Table.Td>{OLAY_AKIS_ETIKET[olay.akis]}</Table.Td>
            <Table.Td>{olay.workplaceAd}</Table.Td>
            <Table.Td>{tarihGoster(olay.olayTarihi)}</Table.Td>
            <Table.Td>
              <Badge variant="light" data-testid={`olay-durum-${olay.takipNo}`}>
                {olayDurumEtiketi(olay.durum, olay.akis, olay.kapali)}
              </Badge>
            </Table.Td>
            <Table.Td>
              <DofSayaci
                toplam={olay.toplamDofSayisi}
                acik={olay.acikDofSayisi}
                testId={`olay-dof-${olay.takipNo}`}
              />
            </Table.Td>
            <Table.Td>
              <Group gap="xs">
                {/* Majör araştırma = TÜRETİLMİŞ İŞARET (rozet; ayrı ekran yok). */}
                {olay.majorArastirma && (
                  <Badge color={ANLAM.engel} data-testid={`olay-major-${olay.takipNo}`}>
                    Majör araştırma
                  </Badge>
                )}
                {/* R13 (F3B·S5): "dosya eksik" da türetilmiş işaret — sunucudan. */}
                {olay.dosyaEksik && (
                  <Badge color={ANLAM.dikkat} data-testid={`olay-dosya-eksik-${olay.takipNo}`}>
                    Dosya eksik
                  </Badge>
                )}
                {olay.anonim && <Badge color={ANLAM.bilgi}>Anonim</Badge>}
              </Group>
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}

/**
 * Anonim bildirim QR'ı üretme (F3B·S4 — ✅ madde 64 akşam kararı: yetki
 * `olay_inceleme` listesi + bahçe; asıl kapı SUNUCUDA — 403 aynı kapıdan).
 * Token/karar sunucudan gelir; kare + PNG İSTEMCİDE çizilir (IzinQrScreen
 * deseni — QR yalnız ADRES taşır). Baskı operasyonu AÇIK MADDE — yalnız
 * indirme.
 */
function QrUretModal({
  acik,
  kapat,
  workplaces,
  anahtarAcik,
}: {
  acik: boolean;
  kapat: () => void;
  workplaces: { id: string; ad: string; companyAd: string }[];
  anahtarAcik: boolean;
}): React.JSX.Element {
  const uret = useQrUret();
  const [workplaceId, setWorkplaceId] = useState<string | null>(null);

  return (
    <Modal opened={acik} onClose={kapat} title="Anonim bildirim QR'ı" size="md">
      <Stack gap="sm">
        {!anahtarAcik && (
          <Alert color={ANLAM.dikkat} data-testid="qr-anahtar-kapali">
            Anonim bildirim kanalı şu anda kapalı (`anonim_bildirim_acik`) — QR üretilebilir ama
            sayfa "kapalı" cevabı verir; kanalı holding yöneticisi açar.
          </Alert>
        )}
        <Select
          label="İşyeri"
          searchable
          data-testid="qr-uret-isyeri"
          data={workplaces.map((w) => ({ value: w.id, label: `${w.ad} (${w.companyAd})` }))}
          value={workplaceId}
          onChange={setWorkplaceId}
        />
        {uret.isError && (
          <Text size="sm" c={ANLAM.engel} role="alert" data-testid="qr-uret-hata">
            {uret.error.message}
          </Text>
        )}
        <Group>
          <Button
            data-testid="qr-uret"
            disabled={!workplaceId}
            loading={uret.isPending}
            onClick={() => workplaceId && uret.mutate({ workplaceId })}
          >
            Üret
          </Button>
        </Group>
        {uret.isSuccess && <QrSonucu link={uret.data.link} yer={uret.data.workplaceAd} />}
      </Stack>
    </Modal>
  );
}

function QrSonucu({ link, yer }: { link: string; yer: string }): React.JSX.Element {
  const qr = qrcode(0, 'M');
  qr.addData(link);
  qr.make();
  const svg = qr.createSvgTag({ cellSize: 4, margin: 2 });

  const pngIndir = (): void => {
    // SVG → canvas → PNG (istemcide; sunucuya görüntü çizdirilmez).
    const img = new Image();
    const svgBlob = new Blob([svg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(svgBlob);
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width * 2;
      canvas.height = img.height * 2;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const a = document.createElement('a');
        a.href = canvas.toDataURL('image/png');
        a.download = `anonim-bildirim-qr-${yer.replaceAll(' ', '-')}.png`;
        a.click();
      }
      URL.revokeObjectURL(url);
    };
    img.src = url;
  };

  return (
    <Stack gap="xs" align="center">
      <div data-testid="qr-uret-karesi" dangerouslySetInnerHTML={{ __html: svg }} />
      <Text
        size="xs"
        c="dimmed"
        ta="center"
        data-testid="qr-uret-link"
        style={{ wordBreak: 'break-all' }}
      >
        {link}
      </Text>
      <Group>
        <Button size="xs" variant="light" data-testid="qr-png-indir" onClick={pngIndir}>
          PNG indir
        </Button>
      </Group>
    </Stack>
  );
}
